import { getAutogeneratedBanner } from '../banners';
import {
  BLOB_ENCRYPTED_MSG,
  BLOB_MISSING_MSG,
  ENCRYPTED_BLOB_PREFIX,
  escapeBlockCommentEnd,
  getEnumMemberKind,
  getFieldDocLines,
  getSensitiveKeys,
  IDENTIFIER_SAFE_KEY,
  partitionRepresentableKeys,
  skippedKeysComment,
  type CoercedType,
  type ResolvedFieldType,
} from '../shared';

// PHP single-quoted string literal: only `\` and `'` need escaping (no interpolation)
function phpSingleQuote(text: string): string {
  return `'${text.replace(/\\/g, '\\\\').replace(/'/g, "\\'")}'`;
}

function phpBaseType(coerced: CoercedType): string {
  if (coerced === 'int') return 'int';
  if (coerced === 'number') return 'float';
  if (coerced === 'boolean') return 'bool';
  if (coerced === 'object') return 'array';
  // an enum field gets its members' scalar type (refined to literals via @var) — the blob carries
  // the *coerced* value, so a numeric enum must be numeric or strict_types throws at runtime
  if (typeof coerced === 'object' && 'enum' in coerced && coerced.enum.length) {
    const kind = getEnumMemberKind(coerced.enum);
    if (kind === 'int') return 'int';
    if (kind === 'number') return 'float';
    if (kind === 'boolean') return 'bool';
    // mixed-kind enum → a union of the member kinds actually present (PHP 8 union type)
    if (kind === 'mixed') {
      const phpKinds = new Set(coerced.enum.map((option) => {
        if (typeof option === 'string') return 'string';
        if (typeof option === 'boolean') return 'bool';
        return Number.isInteger(option) ? 'int' : 'float';
      }));
      return (['string', 'int', 'float', 'bool'] as const).filter((k) => phpKinds.has(k)).join('|');
    }
  }
  // typed arrays/records both decode to PHP arrays; the @var refinement carries the detail
  if (typeof coerced === 'object' && ('arrayOf' in coerced || 'recordOf' in coerced)) {
    return 'array';
  }
  return 'string';
}

function phpEnumLiteral(option: string | number | boolean): string {
  if (typeof option === 'string') return JSON.stringify(option);
  if (typeof option === 'boolean') return option ? 'true' : 'false';
  return String(option);
}

/** phpstan `@var` refinement when the PHP type hint is broader than the real type (enums, objects). */
function phpVarType(coerced: CoercedType): string | undefined {
  if (coerced === 'object') return 'array<string, mixed>';
  if (typeof coerced === 'object' && 'enum' in coerced && coerced.enum.length) {
    return coerced.enum.map(phpEnumLiteral).join('|');
  }
  if (typeof coerced === 'object' && 'arrayOf' in coerced) {
    const inner = phpVarType(coerced.arrayOf) ?? phpBaseType(coerced.arrayOf);
    return `array<int, ${inner}>`;
  }
  if (typeof coerced === 'object' && 'recordOf' in coerced) {
    const { values } = coerced.recordOf;
    const inner = values ? (phpVarType(values) ?? phpBaseType(values)) : 'mixed';
    return `array<string, ${inner}>`;
  }
  return undefined;
}

function phpConstructorParam(field: ResolvedFieldType): Array<string> {
  const baseType = phpBaseType(field.coerced);
  // the `?T` shorthand is not allowed on union types — those spell out `|null` instead
  let typeHint = baseType;
  if (!field.isRequired) {
    typeHint = baseType.includes('|') ? `${baseType}|null` : `?${baseType}`;
  }
  let varType = phpVarType(field.coerced);
  if (varType && !field.isRequired) varType = `${varType}|null`;

  const docLines = getFieldDocLines(field);
  const doc: Array<string> = [];
  if (docLines.length || varType) {
    doc.push('        /**');
    for (const line of docLines) doc.push(`         * ${escapeBlockCommentEnd(line)}`);
    // enum options / object shapes flow into @var — escape them too so a `*/` can't break out
    if (varType) doc.push(`         * @var ${escapeBlockCommentEnd(varType)}`);
    doc.push('         */');
  }
  return [...doc, `        public readonly ${typeHint} $${field.key}${field.isRequired ? '' : ' = null'},`];
}

// one or more identifiers joined by backslashes, e.g. `App` or `App\Config`
const PHP_NAMESPACE = /^[A-Za-z_][A-Za-z0-9_]*(\\[A-Za-z_][A-Za-z0-9_]*)*$/;

// words PHP won't accept as a class name or namespace segment (case-insensitive): keywords
// (`class List` is a parse error) plus reserved type names (`class String` is a fatal error)
const PHP_RESERVED_WORDS = new Set([
  // keywords
  'abstract',
  'and',
  'array',
  'as',
  'break',
  'callable',
  'case',
  'catch',
  'class',
  'clone',
  'const',
  'continue',
  'declare',
  'default',
  'die',
  'do',
  'echo',
  'else',
  'elseif',
  'empty',
  'enddeclare',
  'endfor',
  'endforeach',
  'endif',
  'endswitch',
  'endwhile',
  'eval',
  'exit',
  'extends',
  'final',
  'finally',
  'fn',
  'for',
  'foreach',
  'function',
  'global',
  'goto',
  'if',
  'implements',
  'include',
  'include_once',
  'instanceof',
  'insteadof',
  'interface',
  'isset',
  'list',
  'match',
  'namespace',
  'new',
  'or',
  'print',
  'private',
  'protected',
  'public',
  'require',
  'require_once',
  'return',
  'static',
  'switch',
  'throw',
  'trait',
  'try',
  'unset',
  'use',
  'var',
  'while',
  'xor',
  'yield',
  // reserved class/type names
  'bool',
  'false',
  'float',
  'int',
  'iterable',
  'mixed',
  'never',
  'null',
  'object',
  'parent',
  'self',
  'string',
  'true',
  'void',
]);

function isPhpReservedWord(name: string): boolean {
  return PHP_RESERVED_WORDS.has(name.toLowerCase());
}

export function generatePhpEnvSrc(
  fields: Array<ResolvedFieldType>,
  opts?: { namespace?: unknown, className?: unknown },
): string {
  // class covers representable keys; skipped keys stay as raw env vars (getenv)
  const { safe, skipped } = partitionRepresentableKeys(fields);

  // optional `class=` / `namespace=` for Composer/PSR-4 projects (default: global `Env`)
  const className = opts?.className === undefined ? 'Env' : String(opts.className);
  if (!IDENTIFIER_SAFE_KEY.test(className) || isPhpReservedWord(className)) {
    throw new Error(`@generatePhpEnv - \`class\` must be a valid PHP class name (got ${JSON.stringify(opts?.className)})`);
  }
  let namespace: string | undefined;
  if (opts?.namespace !== undefined && String(opts.namespace).trim() !== '') {
    namespace = String(opts.namespace).trim();
    if (!PHP_NAMESPACE.test(namespace) || namespace.split('\\').some(isPhpReservedWord)) {
      throw new Error(`@generatePhpEnv - \`namespace\` must be a valid PHP namespace (got ${JSON.stringify(opts?.namespace)})`);
    }
  }

  // SENSITIVE_KEYS covers every sensitive key (incl. skipped ones) so redaction doesn't miss them
  const sensitiveKeys = getSensitiveKeys(fields);
  // a PHP constructor cannot declare an optional (defaulted) param before a required one
  const ordered = [...safe].sort((a, b) => Number(b.isRequired) - Number(a.isRequired));

  const src = [
    '<?php',
    getAutogeneratedBanner('//'),
    '',
    // per-file; imposes nothing on callers, but turns blob/schema drift into a clean TypeError.
    // must precede the namespace declaration.
    'declare(strict_types=1);',
    ...(namespace ? ['', `namespace ${namespace};`] : []),
    '',
    ...skippedKeysComment(skipped, '//'),
    `final class ${className}`,
    '{',
    '    /** @var list<string> */',
    `    public const SENSITIVE_KEYS = [${sensitiveKeys.map((k) => `'${k}'`).join(', ')}];`,
    '',
    '    public function __construct(',
    ...ordered.flatMap(phpConstructorParam),
    '    ) {}',
    '',
    '    /** Parse the __VARLOCK_ENV blob (injected by `varlock run`) into a typed Env. */',
    '    public static function load(): self',
    '    {',
    "        $blob = getenv('__VARLOCK_ENV');",
    '        if ($blob === false || $blob === \'\') {',
    `            throw new \\RuntimeException(${phpSingleQuote(BLOB_MISSING_MSG)});`,
    '        }',
    `        if (str_starts_with($blob, ${JSON.stringify(ENCRYPTED_BLOB_PREFIX)})) {`,
    `            throw new \\RuntimeException(${phpSingleQuote(BLOB_ENCRYPTED_MSG)});`,
    '        }',
    "        $cfg = json_decode($blob, true, flags: JSON_THROW_ON_ERROR)['config'];",
    '        return new self(',
    // required keys throw a clear error if absent (stale generated file); optional keys default to null
    ...safe.map((f) => `            ${f.key}: $cfg['${f.key}']['value'] ?? ${f.isRequired
      ? `throw new \\RuntimeException('varlock: missing required key ${f.key} in __VARLOCK_ENV')`
      : 'null'},`),
    '        );',
    '    }',
    '}',
    '',
  ];

  return src.join('\n');
}
